import React, { useEffect, useState, useRef } from 'react';

const COUNTDOWN_SECONDS = 60; // matches WARN_BEFORE_MS in useIdleTimeout

/**
 * IdleWarningModal
 *
 * Shown when the user has been idle for 9 minutes.
 * Counts down 60 seconds and calls onLogout when it reaches 0.
 * Calling onStayLoggedIn resets the idle timer via the parent.
 */
const IdleWarningModal = ({ isOpen, onStayLoggedIn, onLogout }) => {
    const [secondsLeft, setSecondsLeft] = useState(COUNTDOWN_SECONDS);
    const intervalRef = useRef(null);

    useEffect(() => {
        if (!isOpen) {
            setSecondsLeft(COUNTDOWN_SECONDS);
            if (intervalRef.current) clearInterval(intervalRef.current);
            return;
        }

        setSecondsLeft(COUNTDOWN_SECONDS);
        intervalRef.current = setInterval(() => {
            setSecondsLeft(prev => {
                if (prev <= 1) {
                    clearInterval(intervalRef.current);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
        };
    }, [isOpen]);

    if (!isOpen) return null;

    return (
        <div style={overlayStyle}>
            <div style={modalStyle}>
                <div style={iconStyle}>⏱️</div>
                <h2 style={titleStyle}>Session Expiring Soon</h2>
                <p style={bodyStyle}>
                    You have been inactive for 9 minutes.<br />
                    You will be automatically signed out in:
                </p>
                <div style={countdownStyle}>{secondsLeft}s</div>
                <p style={subTextStyle}>
                    Click <strong>Stay Logged In</strong> to continue your session.
                </p>
                <div style={buttonRowStyle}>
                    <button
                        onClick={onStayLoggedIn}
                        style={stayBtnStyle}
                        onMouseEnter={e => (e.currentTarget.style.background = '#1d4ed8')}
                        onMouseLeave={e => (e.currentTarget.style.background = '#2563eb')}
                    >
                        Stay Logged In
                    </button>
                    <button
                        onClick={onLogout}
                        style={logoutBtnStyle}
                        onMouseEnter={e => (e.currentTarget.style.background = '#b91c1c')}
                        onMouseLeave={e => (e.currentTarget.style.background = '#dc2626')}
                    >
                        Sign Out Now
                    </button>
                </div>
            </div>
        </div>
    );
};

/* ── inline styles ─────────────────────────────────────────────────────────── */

const overlayStyle = {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.55)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    backdropFilter: 'blur(2px)',
};

const modalStyle = {
    background: '#ffffff',
    borderRadius: 16,
    padding: '40px 36px 32px',
    maxWidth: 400,
    width: '90%',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
    textAlign: 'center',
    fontFamily: 'inherit',
};

const iconStyle = {
    fontSize: 48,
    marginBottom: 12,
};

const titleStyle = {
    margin: '0 0 12px',
    fontSize: 22,
    fontWeight: 700,
    color: '#1e293b',
};

const bodyStyle = {
    margin: '0 0 8px',
    fontSize: 15,
    color: '#475569',
    lineHeight: 1.6,
};

const countdownStyle = {
    fontSize: 52,
    fontWeight: 800,
    color: '#dc2626',
    margin: '8px 0 12px',
    letterSpacing: '-1px',
};

const subTextStyle = {
    fontSize: 13,
    color: '#64748b',
    margin: '0 0 24px',
};

const buttonRowStyle = {
    display: 'flex',
    gap: 12,
    justifyContent: 'center',
};

const stayBtnStyle = {
    background: '#2563eb',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    padding: '11px 22px',
    fontSize: 15,
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'background 0.2s',
};

const logoutBtnStyle = {
    background: '#dc2626',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    padding: '11px 22px',
    fontSize: 15,
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'background 0.2s',
};

export default IdleWarningModal;
